/**
 * Storage API Service
 *
 * Provides API calls for managing organization-level storage configuration
 * and eval data files.
 */
import api from './api';

// Types

export interface StorageConfig {
  id: string;
  organization_id: string;
  is_enabled: boolean;
  max_total_size_mb: number;
  max_file_count: number;
  current_size_bytes: number;
  current_file_count: number;
  usage_percentage: number;
  file_count_percentage: number;
  s3_prefix: string;
  created_at: string;
  updated_at: string;
}

export interface StorageConfigUpdate {
  is_enabled?: boolean;
  max_total_size_mb?: number;
  max_file_count?: number;
}

export interface StorageStatus {
  is_enabled: boolean;
  current_size_bytes: number;
  current_file_count: number;
  max_total_size_mb: number;
  max_file_count: number;
  usage_percentage: number;
  file_count_percentage: number;
  s3_prefix: string;
}

export interface DataFile {
  id: string;
  organization_id: string;
  name: string;
  filename: string;
  description?: string;
  file_size: number;
  content_type: string;
  file_type: 'csv' | 'json' | 'text';
  usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by_username?: string;
}

export interface DataFileContent {
  id: string;
  name: string;
  content_type: string;
  content?: string;
  url?: string;
}

export interface DataFilePreview {
  id: string;
  name: string;
  file_type: string;
  preview: string;
  truncated: boolean;
}

export interface DataFileUploadResult {
  file: DataFile;
  message: string;
}

// Storage Config API Functions

/**
 * Get the organization's storage configuration.
 */
export async function getStorageConfig(): Promise<StorageConfig> {
  const response = await api.get<StorageConfig>('/settings/storage');
  return response.data;
}

/**
 * Update the organization's storage configuration.
 */
export async function updateStorageConfig(data: StorageConfigUpdate): Promise<StorageConfig> {
  const response = await api.put<StorageConfig>('/settings/storage', data);
  return response.data;
}

/**
 * Get the organization's storage usage status.
 */
export async function getStorageStatus(): Promise<StorageStatus> {
  const response = await api.get<StorageStatus>('/settings/storage/status');
  return response.data;
}

// Data File API Functions

/**
 * List all data files for the organization.
 */
export async function listDataFiles(includeInactive = false): Promise<DataFile[]> {
  const response = await api.get<{ files: DataFile[]; total: number }>('/eval-data-files', {
    params: { include_inactive: includeInactive },
  });
  return response.data.files;
}

/**
 * Get a specific data file by ID.
 */
export async function getDataFile(fileId: string): Promise<DataFile> {
  const response = await api.get<DataFile>(`/eval-data-files/${fileId}`);
  return response.data;
}

/**
 * Upload a new data file.
 */
export async function uploadDataFile(
  file: File,
  name: string,
  description?: string
): Promise<DataFileUploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  if (description) {
    formData.append('description', description);
  }

  const response = await api.post<DataFileUploadResult>('/eval-data-files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Get content of a data file (as URL or direct content).
 */
export async function getDataFileContent(fileId: string, asUrl = true): Promise<DataFileContent> {
  const response = await api.get<DataFileContent>(`/eval-data-files/${fileId}/content`, {
    params: { as_url: asUrl },
  });
  return response.data;
}

/**
 * Preview the first N lines of a data file.
 */
export async function previewDataFile(fileId: string, maxLines = 10): Promise<DataFilePreview> {
  const response = await api.get<DataFilePreview>(`/eval-data-files/${fileId}/preview`, {
    params: { max_lines: maxLines },
  });
  return response.data;
}

/**
 * Delete a data file.
 */
export async function deleteDataFile(
  fileId: string,
  hardDelete = false
): Promise<{ message?: string; warning?: string; function_count?: number; action_required?: string }> {
  const response = await api.delete(`/eval-data-files/${fileId}`, {
    params: { hard_delete: hardDelete },
  });
  return response.data;
}

// Utility functions

/**
 * Format bytes to human readable string.
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file type icon name.
 */
export function getFileTypeIcon(fileType: string): string {
  switch (fileType) {
    case 'csv':
      return 'file-spreadsheet';
    case 'json':
      return 'file-json';
    case 'text':
    default:
      return 'file-text';
  }
}
